// pages/FormViewerPage/hooks/useFormViewer/effectsFunnel.ts — AddToCart saat nama+WA valid
//
// Peta baku: ATC = interaksi nyata pertama (nama + WA valid, 1x per sesi).
// JANGAN tembak saat auto-select varian / page load — itu mencemari sinyal.
import { useEffect } from 'react';
import { validateWhatsappNumber } from '../../services/formViewerService/validation';
import { fireFunnelMetaEvent, fireFunnelGoogleEvent, fireFunnelTiktokEvent, fireFunnelSnackEvent } from '../../services/formViewerService/funnelEvents';
import { pickPlatformPixelIds } from '../../services/formViewerService/pixelOptimization';
import { buildMetaUserDataRaw } from '../../../../utils/pixelPii';
import { getPersistentExternalId } from '../../../../services/attributionIds';
import { fireGoogleAdsConversion } from '../../../../utils/googleAdsConversion';
import type { FormViewerState } from './state';

export function useFormViewerFunnelEffects(s: FormViewerState) {
    const { form, identifier, customerData, quantity, formPagePixels, formPagePixelConfigs, curComboRef } = s;
    const name = customerData.name;
    const whatsapp = customerData.whatsapp;

    useEffect(() => {
        // Jangan gate ke Meta saja — form dengan Google/TikTok tanpa Meta tetap
        // harus kirim ATC ke platform yg punya pixel (caller masing-masing no-op
        // kalau daftar kosong).
        if (!form || (formPagePixels.ids.length === 0 && (formPagePixelConfigs?.length || 0) === 0)) return;
        // customerFields diketik longgar (Record) seperti validation.ts — minLength custom per form.
        const fields: Record<string, any> = (form as any)?.customerFields || {};
        const minName = fields?.name?.minLength || 3;
        if (!name || name.trim().length < minName) return;
        if (!validateWhatsappNumber(whatsapp || '').isValid) return;
        const unit = curComboRef.current?.sellingPrice ?? form?.productPrice ?? 0;
        // Init pertama menang: bawa PII terisi (nama+WA) via kanal init resmi.
        const atcUd = buildMetaUserDataRaw({ name: name || undefined, phone: whatsapp || undefined, country: 'id', externalId: getPersistentExternalId() }) || undefined;
        fireFunnelMetaEvent(formPagePixels.ids, 'AddToCart', {
            content_ids: [form?.slug || identifier],
            content_name: form?.title || 'Form',
            value: unit * (quantity || 1),
        }, `px_funnel_atc_${form.id}`, atcUd);
        // Mid-funnel platform lain (Google/TikTok/Snack) — daftar dari configs
        // yg sama (sudah termasuk pixel optimasi brand). Gagal tracking abaikan.
        try {
            const cfgs = formPagePixelConfigs || [];
            const val = unit * (quantity || 1);
            const base = {
                content_ids: [form?.slug || identifier],
                content_name: form?.title || 'Form',
                value: val,
            };
            fireFunnelGoogleEvent(pickPlatformPixelIds(cfgs, 'google'), 'add_to_cart', { value: val }, `px_funnel_gatc_${form.id}`);
            // Google Ads (AW-) conversion snippet — di-fire DI SINI (bukan saat load).
            fireGoogleAdsConversion(form, 'atc', { value: val, dedupeKey: form.id });
            fireFunnelTiktokEvent(pickPlatformPixelIds(cfgs, 'tiktok'), 'AddToCart', base, `px_funnel_ttatc_${form.id}`);
            fireFunnelSnackEvent(pickPlatformPixelIds(cfgs, 'snack'), 'ADD_CART', base, `px_funnel_snatc_${form.id}`);
        } catch { /* abaikan */ }
    }, [form, identifier, name, whatsapp, quantity, formPagePixels, formPagePixelConfigs]);
}
